import type { NameType, NameStyle, NameSubcategory } from '@creanizer/types'
import type { CharacterInput, CharacterResult, SavedCharacter } from '../../../../types/character' // temp fix
import type { CustomValue } from '@creanizer/utils'
import { SubcategorySelector } from './SubcategorySelector'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useState, useEffect } from 'react'

interface NameGeneratorFormProps {
  nameType: NameType
  setNameType: (value: NameType) => void
  customNameType: string
  setCustomNameType: (value: string) => void
  subcategory: NameSubcategory | undefined
  setSubcategory: (value: NameSubcategory | undefined) => void
  customSubcategory: string
  setCustomSubcategory: (value: string) => void
  style: NameStyle
  setStyle: (value: NameStyle) => void
  customStyle: string
  setCustomStyle: (value: string) => void
  savedStyles: CustomValue[]
  savedSubcategories: CustomValue[]
  savedNameTypes: CustomValue[]
  addCustomValue: (type: CustomValue['type'], value: string, label: string, generatorType?: CustomValue['generatorType'], nameType?: string) => Promise<void>
  useCustomValue: (id: string) => Promise<void>
  worldId?: string
  setWorldId: (value: string | undefined) => void
  worldContext?: string
  setWorldContext: (value: string | undefined) => void
}

export function NameGeneratorForm({
  nameType,
  setNameType,
  customNameType,
  setCustomNameType,
  subcategory,
  setSubcategory,
  customSubcategory,
  setCustomSubcategory,
  style,
  setStyle,
  customStyle,
  setCustomStyle,
  savedStyles,
  savedSubcategories,
  savedNameTypes,
  addCustomValue,
  useCustomValue,
  worldId,
  setWorldId,
  worldContext,
  setWorldContext,
}: NameGeneratorFormProps) {
  // 保存済み世界観を取得
  const { savedWorlds } = useWorldBuilderStore()
  
  // 世界観が選択されたときのハンドラ
  const handleWorldSelect = (selectedWorldId: string) => {
    setWorldId(selectedWorldId)
    
    if (selectedWorldId) {
      const world = savedWorlds.find(w => w.id === selectedWorldId)
      if (world) {
        // 世界観のコンテキストを構築
        const context = `
世界名: ${world.result.name}
概要: ${world.result.summary}
時代: ${world.result.details?.era?.name || '不明'} - ${world.result.details?.era?.description || ''}
技術レベル: ${world.result.parameters?.scienceLevel || 5}/10
魔法レベル: ${world.result.parameters?.magicLevel || 5}/10
文明レベル: ${world.result.parameters?.civilizationLevel || 5}/10

文化:
${world.result.details?.cultures?.map(c => `- ${c.name}: ${c.description}`).join('\n') || '不明'}

言語体系: ${world.result.details?.cultures?.[0]?.language || '不明'}
命名規則: ${world.result.details?.cultures?.[0]?.customs?.join('、') || '一般的な命名規則に従う'}
        `.trim()
        
        setWorldContext(context)
      }
    } else {
      setWorldContext(undefined)
    }
  }
  return (
    <div className="space-y-4 mb-6">
      <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wider">
        基本設定
      </h3>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            名前タイプ
          </label>
          <select
            className="input"
            value={customNameType ? 'custom' : nameType}
            onChange={(e) => {
              if (e.target.value === 'custom') {
                setNameType('custom' as NameType)
                setCustomNameType('')
              } else {
                setCustomNameType('')
                setNameType(e.target.value as NameType)
              }
            }}
          >
            <option value="person_full">人名（フルネーム）</option>
            <option value="person_family">人名（名字のみ）</option>
            <option value="person_given">人名（名前のみ）</option>
            <option value="weapon">武器名</option>
            <option value="skill">スキル名</option>
            <option value="place">地名</option>
            <option value="organization">組織名</option>
            <option value="item">アイテム名</option>
            <option value="magic">魔法名</option>
            <option value="title">称号</option>
            <option value="enemy">エネミー名</option>
            <option value="custom">カスタム...</option>
          </select>
          {(customNameType || nameType === ('custom' as NameType)) && (
            <div className="mt-2 space-y-2">
              <input
                type="text"
                className="input"
                placeholder="カスタム名前タイプを入力（例: 呪文名、都市名など）"
                value={customNameType}
                onChange={(e) => setCustomNameType(e.target.value)}
              />
              {customNameType && (
                <button
                  type="button"
                  onClick={async () => {
                    await addCustomValue('nameType' as any, customNameType, customNameType, 'name')
                    alert(`カスタム名前タイプ "${customNameType}" を保存しました`)
                  }}
                  className="btn btn-secondary text-sm"
                >
                  💾 保存
                </button>
              )}
            </div>
          )}
          {/* 保存済みカスタム名前タイプ */}
          {savedNameTypes.length > 0 && (
            <div className="mt-2">
              <label className="text-xs text-gray-600">保存済みカスタム:</label>
              <div className="flex flex-wrap gap-1 mt-1">
                {savedNameTypes.map(cv => (
                  <button
                    key={cv.id}
                    type="button"
                    onClick={() => {
                      setNameType('custom' as NameType)
                      setCustomNameType(cv.value)
                      useCustomValue(cv.id)
                    }}
                    className="px-2 py-1 text-xs bg-purple-100 hover:bg-purple-200 rounded"
                  >
                    {cv.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* サブカテゴリ選択 */}
        <SubcategorySelector
          nameType={nameType}
          subcategory={subcategory}
          setSubcategory={setSubcategory}
          customSubcategory={customSubcategory}
          setCustomSubcategory={setCustomSubcategory}
          savedSubcategories={savedSubcategories}
          addCustomValue={addCustomValue}
          useCustomValue={useCustomValue}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            スタイル
          </label>
          <select
            className="input"
            value={style}
            onChange={(e) => setStyle(e.target.value as NameStyle)}
          >
            <option value="modern_japanese">現代日本風</option>
            <option value="japanese">和風（伝統的）</option>
            <option value="western">西洋風</option>
            <option value="fantasy">中世ファンタジー風</option>
            <option value="sf">SF風</option>
            <option value="cyberpunk">サイバーパンク風</option>
            <option value="conlang">架空言語ファンタジー</option>
            <option value="chinese">中国風</option>
            <option value="ancient">古代風</option>
            <option value="mythical">神話風</option>
            <option value="custom">カスタム...</option>
          </select>
          {style === 'custom' && (
            <div className="mt-2 space-y-2">
              <input
                type="text"
                className="input"
                placeholder="カスタムスタイルを入力（例: スチームパンク風）"
                value={customStyle}
                onChange={(e) => setCustomStyle(e.target.value)}
              />
              {customStyle && (
                <button
                  type="button"
                  onClick={async () => {
                    await addCustomValue('style', customStyle, customStyle, 'name')
                    alert(`カスタムスタイル "${customStyle}" を保存しました`)
                  }}
                  className="btn btn-secondary text-sm"
                >
                  💾 保存
                </button>
              )}
            </div>
          )}
          {/* 保存済みカスタムスタイル */}
          {savedStyles.length > 0 && (
            <div className="mt-2">
              <label className="text-xs text-gray-600">保存済みカスタム:</label>
              <div className="flex flex-wrap gap-1 mt-1">
                {savedStyles.map(cv => (
                  <button
                    key={cv.id}
                    type="button"
                    onClick={() => {
                      setStyle('custom')
                      setCustomStyle(cv.value)
                      useCustomValue(cv.id)
                    }}
                    className="px-2 py-1 text-xs bg-purple-100 hover:bg-purple-200 rounded"
                  >
                    {cv.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* 世界観から生成 */}
        <div>
          <label className="block text-sm font-medium mb-2">
            世界観から生成（オプション）
          </label>
          <select
            className="input"
            value={worldId || ''}
            onChange={(e) => handleWorldSelect(e.target.value)}
          >
            <option value="">選択しない</option>
            {savedWorlds.map(world => (
              <option key={world.id} value={world.id}>
                {world.title}
              </option>
            ))}
          </select>
          {worldId && (
            <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
              <div className="font-medium text-blue-900 mb-1">選択中の世界観</div>
              <div className="text-blue-700">
                {savedWorlds.find(w => w.id === worldId)?.result.summary?.substring(0, 100)}...
              </div>
              <div className="text-blue-600 mt-1">
                ※ 世界観の文化や言語体系に基づいた名前が生成されます
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}